// LiveChatComponent.tsx - Widget de Chat/WhatsApp
// Botão flutuante para iniciar conversa via WhatsApp ou chat

import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, X, Send, Phone, Clock, User,
  ChevronDown, Smile, Paperclip
} from 'lucide-react';

interface LiveChatData {
  title?: string;
  subtitle?: string;
  welcomeMessage?: string;
  whatsappNumber: string;
  whatsappDefaultMessage?: string;
  operatorName?: string;
  operatorAvatar?: string;
  operatorRole?: string;
  businessHours?: {
    start: string; // "08:00"
    end: string;   // "18:00"
    days: number[]; // 0-6 (Dom-Sab)
    timezone?: string;
  };
  offlineMessage?: string;
  showOnMobile?: boolean;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  quickReplies?: string[];
  style?: 'bubble' | 'expanded' | 'minimal';
}

interface Props {
  data: LiveChatData;
  settings?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export const LiveChatComponent: React.FC<Props> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const {
    title = 'Fale Conosco',
    subtitle = 'Estamos online! Como podemos ajudar?',
    welcomeMessage = 'Olá! 👋 Seja bem-vindo! Como posso ajudar você hoje?',
    whatsappNumber,
    whatsappDefaultMessage = 'Olá! Gostaria de mais informações.',
    operatorName = 'Atendimento',
    operatorAvatar,
    operatorRole = 'Suporte ao Cliente',
    businessHours,
    offlineMessage = 'No momento estamos fora do horário de atendimento. Deixe sua mensagem que retornaremos assim que possível.',
    position = 'bottom-right',
    primaryColor = '#25D366',
    quickReplies = ['Quero fazer uma reserva', 'Valores e pacotes', 'Localização', 'Outras informações'],
    style = 'bubble',
  } = data;

  // Verificar se está no horário de atendimento
  const isOnline = (): boolean => {
    if (!businessHours) return true;

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 100 + now.getMinutes();

    if (!businessHours.days.includes(currentDay)) return false;

    const [startHour, startMin] = businessHours.start.split(':').map(Number);
    const [endHour, endMin] = businessHours.end.split(':').map(Number);
    const startTime = startHour * 100 + startMin;
    const endTime = endHour * 100 + endMin;

    return currentTime >= startTime && currentTime <= endTime;
  };

  const online = isOnline();

  // Adicionar mensagem de boas-vindas quando abrir
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMsg: Message = {
        id: '1',
        text: online ? welcomeMessage : offlineMessage,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages([welcomeMsg]);
    }
  }, [isOpen]);

  // Mostrar notificação após alguns segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setHasNewMessage(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;

    // Adicionar mensagem do usuário
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simular resposta do bot
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Obrigado pelo contato! Para um atendimento mais rápido e personalizado, clique no botão abaixo para falar diretamente conosco pelo WhatsApp. 📱',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1500);
  };

  const handleWhatsAppRedirect = (customMessage?: string) => {
    const message = encodeURIComponent(customMessage || whatsappDefaultMessage);
    const url = `https://wa.me/${whatsappNumber}?text=${message}`;
    window.open(url, '_blank');
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Estilo Minimal - apenas botão do WhatsApp
  if (style === 'minimal') {
    return (
      <div 
        className={`fixed ${position === 'bottom-right' ? 'right-6' : 'left-6'} bottom-6 z-50`}
      >
        <button
          onClick={() => handleWhatsAppRedirect()}
          className="group flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          style={{ backgroundColor: primaryColor }}
        >
          <MessageCircle size={24} />
          <span className="font-semibold hidden md:inline">Fale pelo WhatsApp</span>
        </button>
      </div>
    );
  }

  // Estilo Bubble ou Expanded
  return (
    <>
      {/* Widget Container */}
      <div 
        className={`fixed ${position === 'bottom-right' ? 'right-6' : 'left-6'} bottom-6 z-50 flex flex-col items-end`}
      >
        {/* Chat Window */}
        {isOpen && (
          <div 
            className="mb-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
            style={{ width: '380px', maxHeight: '600px' }}
          >
            {/* Header */}
            <div 
              className="p-4 text-white"
              style={{ backgroundColor: primaryColor }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {operatorAvatar ? (
                      <img 
                        src={operatorAvatar} 
                        alt={operatorName}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white/30"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <User className="w-6 h-6" />
                      </div>
                    )}
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${online ? 'bg-green-400' : 'bg-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{operatorName}</h3>
                    <p className="text-sm text-white/80">{online ? 'Online agora' : 'Offline'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition"
                  title="Fechar chat"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-72 overflow-y-auto p-4 bg-gray-50 space-y-4">
              {messages.map((msg) => (
                <div 
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                      msg.sender === 'user' 
                        ? 'bg-blue-600 text-white rounded-br-md' 
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white px-4 py-3 rounded-2xl shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Replies */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Respostas rápidas:</p>
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, i) => (
                    <button
                      key={i}
                      onClick={() => handleSendMessage(reply)}
                      className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-gray-300 transition"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* WhatsApp CTA */}
            <div className="p-4 bg-white border-t">
              <button
                onClick={() => handleWhatsAppRedirect()}
                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition"
              >
                <Phone size={20} />
                Continuar no WhatsApp
              </button>
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputValue)}
                placeholder="Digite sua mensagem..."
                className="flex-1 px-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={() => handleSendMessage(inputValue)}
                disabled={!inputValue.trim()}
                className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                style={{ backgroundColor: primaryColor }}
                title="Enviar mensagem"
              >
                <Send size={18} />
              </button>
            </div>

            {/* Business Hours */}
            {businessHours && (
              <div className="px-4 py-2 bg-gray-50 border-t text-center">
                <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                  <Clock size={12} />
                  Atendimento: {businessHours.start} - {businessHours.end}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Floating Button */}
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            setHasNewMessage(false);
          }}
          className="group relative flex items-center justify-center w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: primaryColor }}
        >
          {isOpen ? (
            <ChevronDown className="w-7 h-7 text-white" />
          ) : (
            <MessageCircle className="w-7 h-7 text-white" />
          )}
          
          {/* Notification Badge */}
          {hasNewMessage && !isOpen && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              1
            </span>
          )}

          {/* Pulse Effect */}
          {!isOpen && (
            <span 
              className="absolute inset-0 rounded-full animate-ping opacity-30"
              style={{ backgroundColor: primaryColor }}
            />
          )}
        </button>

        {/* Tooltip */}
        {!isOpen && (
          <div className="absolute bottom-20 right-0 bg-white px-4 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
            <p className="text-sm text-gray-700 whitespace-nowrap">{title}</p>
            <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-white" />
          </div>
        )}
      </div>

    </>
  );
};
